//angular imports
import {
  Component,
  computed,
  input,
  signal,
  Signal,
  untracked,
  WritableSignal,
} from "@angular/core";
import { 
  FormBuilder,
  FormGroup,
  Validators
} from "@angular/forms";
import { 
  Subject,
  takeUntil
} from "rxjs";

//local imports
import { FormValueModel } from "@app/shared";
import { ProjectModel, ProgramModel, DepartmentModel } from "@app/catalog";

/**
 * @summary A model to hold the state for the current details form.
 */
export class DepartmentDetails {

  /**
 * @summary The current entity code
 */
  public code: FormValueModel<string>;

  /**
   * @summary The current entity name
   */
  public name: FormValueModel<string>;

  /**
   * @summary The current selected program
   */
  public program: FormValueModel<string>;

  /**
   * @summary The current selected project
   */
  public project: FormValueModel<string>;

  /**
   * @summary Indicates if the form has changes.
   */
  public hasChanges: Signal<boolean>;

  /**
   * @summary Indicates if the form has validation issues
   */
  public isValid: WritableSignal<boolean> = signal<boolean>(false);

  /**
   * @summary Indicates if the form is awaiting validation
   */
  public isPending: WritableSignal<boolean> = signal<boolean>(false);

  /**
   * @summary Creates a new Details instance using the provided entity to prepopulate it.
   * @description This is used for the edit form
   * @param entity: the model for the entity being changed
   * @returns An instance of a details model for tracking changes
   */
  public static fromEntity(entity: DepartmentModel) {
    let _this = new DepartmentDetails();

    _this.name = FormValueModel.create(entity.name());

    _this.hasChanges = FormValueModel.hasChanges([_this.name]);

    return _this;
  }

  /**
   * @summary Creates a new Details instance with no data.
   * @description This is used for the add form
   * @returns An instance of a details model for tracking changes
   */
  public static empty() {
    let _this = new DepartmentDetails();

    _this.code = FormValueModel.create<string>(undefined);
    _this.name = FormValueModel.create<string>(undefined);
    _this.program = FormValueModel.create<string>(undefined);
    _this.project = FormValueModel.create<string>(undefined);

    _this.hasChanges = FormValueModel.hasChanges([_this.code, _this.name, _this.program, _this.project]);

    return _this;
  }
}

@Component({
  selector: "department-details",
  templateUrl: "./department-details.component.html",
  styleUrl: "./department-details.component.scss",
  standalone: false,
})
export class DepartmentDetailsComponent {
  private readonly destroying$ = new Subject<void>();

  public department = input.required<DepartmentDetails>();
  public programs = input.required<ProgramModel[]>();
  public projects = input.required<ProjectModel[]>();

  public form: Signal<FormGroup> = computed(() => {
    let entity = this.department();

    let formParts = {
      code: [
        untracked(() => entity?.code.value() || ""),
        {
          validators: [
            Validators.required,
            Validators.pattern("^(?!\\s+$).+"), //Cannot be whitespace
          ],
        },
      ],
      name: [
        untracked(() => entity?.name.value() || ""),
        {
          validators: [
            Validators.required,
            Validators.pattern("^(?!\\s+$).+"), //Cannot be whitespace
          ],
        },
      ],
      program: [untracked( () => entity?.program.value() || "" ), {        
        validators: [
          Validators.required,
          Validators.nullValidator,
          Validators.pattern('^(?!\\s+$).+') //Cannot be whitespace
        ]
      }],
      project: [untracked( () => entity?.project.value() || "" ), {        
        validators: [
          Validators.required,
          Validators.nullValidator,
          Validators.pattern('^(?!\\s+$).+') //Cannot be whitespace
        ]
      }],
    };

    let form = this.formBuilder.group(formParts);

    // Subscribe to form value changes, emit events.
    form.valueChanges.pipe(takeUntil(this.destroying$)).subscribe((_) => {
      let rawValue = form.getRawValue();

      let details = this.department();

      details.code.value.set(rawValue.code);
      details.name.value.set(rawValue.name);
      details.program.value.set(rawValue.program);
      details.project.value.set(rawValue.project);
    });

    //If the form changes, pass the validation status back into the model
    //
    //NB: Setting the signal in a computed signal can cause problems if the value is set before the signal is complete.
    //    In this case it doesn't occur because the validation is changed by the async validator configured on the form, it has a
    //    debounceTime of 500ms so this is enough to allow this computed signal to complete.
    form.statusChanges.pipe(takeUntil(this.destroying$)).subscribe((_) => {
      let details = this.department();

      if (form.pending) {
        details.isPending.set(true);
        return;
      }

      details.isPending.set(false);
      details.isValid.set(form.valid);
    });

    return form;
  });

  constructor(private formBuilder: FormBuilder) {}

  ngOnDestroy(): void {
    this.destroying$.next(undefined);
    this.destroying$.complete();
  }
}
